import { useEffect, useState } from "react";
import useSWR from "swr";
import { APIError } from "@limrun/api/core/error";
import { useAuth } from "@/auth/auth";
import { orgRequestOptions } from "@/client/director/org";
import { cacheDuration } from "@/lib/build-cache";
import { Badge } from "@/components/ui/badge";

type CachePhase = {
	phase: string;
	message?: string;
	startedAt?: string;
	finishedAt?: string;
};
type CacheStatus = { config?: { key?: string }; restore: CachePhase; save: CachePhase };
const activePhases = new Set(["placing", "downloading", "materializing", "quiescing", "archiving", "uploading", "verifying"]);

export function BuildCacheStatus({ instanceId, organizationTid }: { instanceId: string; organizationTid: string }) {
	const auth = useAuth();
	const { data, error } = useSWR<CacheStatus | null>(
		["buildCache", instanceId, organizationTid],
		async () => {
			try {
				return await auth.regionClient.get<CacheStatus>(
					`/v1/xcode_instances/${encodeURIComponent(instanceId)}/cache`,
					orgRequestOptions(organizationTid),
				);
			} catch (error) {
				// Persisted cache logs remain in history after the regional instance is deleted.
				if (error instanceof APIError && error.status === 404) return null;
				throw error;
			}
		},
		{ refreshInterval: (latest) => latest?.config ? 3_000 : 0 },
	);
	const [, setTick] = useState(0);
	const operations = data
		? ([["Restoration", data.restore], ["Publication", data.save]] as const).filter(([, phase]) => activePhases.has(phase.phase))
		: [];
	const running = operations.length > 0;
	useEffect(() => {
		if (!running) return;
		const timer = setInterval(() => setTick((tick) => tick + 1), 1000);
		return () => clearInterval(timer);
	}, [running]);
	if (error) return <p className="text-sm text-muted-foreground">Cache status is temporarily unavailable.</p>;
	if (!data?.config || !running) return null;
	return (
		<section aria-label="Build cache" className="flex shrink-0 flex-wrap gap-x-6 gap-y-2 rounded-lg border bg-background px-3 py-2">
			{operations.map(([label, phase]) => {
				const duration = cacheDuration(phase.startedAt, phase.finishedAt);
				return (
					<div key={label} className="flex min-w-0 items-center gap-2 text-xs" title={phase.message}>
						<span className="font-medium">Cache {label.toLowerCase()}</span>
						<Badge variant="outline">{phase.phase}</Badge>
						{duration && <span className="text-muted-foreground">{duration}</span>}
					</div>
				);
			})}
		</section>
	);
}
